import React, { useState, useEffect, useCallback, useRef } from "react";
import axiosInstance from "../utils/axiosInstance";
import { motion, AnimatePresence } from "framer-motion";
import SignatureCanvas from "react-signature-canvas";

const fmtDate = (d) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("rw-RW", { year: "numeric", month: "short", day: "numeric" }); }
  catch { return d; }
};

const SigBadge = ({ signed, label }) =>
  signed ? (
    <span className="rz-pill" style={{ display:"inline-flex", alignItems:"center", gap:4, padding:"3px 10px", borderRadius:20, background:"#dcfce7", color:"#15803d", border:"1px solid #bbf7d0", fontSize:"0.72rem", fontWeight:700 }}>✅ {label}</span>
  ) : (
    <span className="rz-pill" style={{ display:"inline-flex", alignItems:"center", gap:4, padding:"3px 10px", borderRadius:20, background:"#fef3c7", color:"#b45309", border:"1px solid #fde68a", fontSize:"0.72rem", fontWeight:700 }}>⏳ {label}</span>
  );

/* ── Sign Modal ── */
const SignModal = ({ report, onClose, onSigned }) => {
  const sigRef = useRef(null);
  const [signing, setSigning] = useState(false);
  const [error, setError] = useState(null);

  const signerName = (() => {
    try { const p = JSON.parse(atob(localStorage.getItem("token").split(".")[1])); return p.fullname || p.name || "Umudugudu"; }
    catch { return "Umudugudu"; }
  })();

  const handleSign = async () => {
    if (!sigRef.current || sigRef.current.isEmpty()) { setError("Shyiraho umukono mbere yo gukomeza."); return; }
    setSigning(true); setError(null);
    try {
      const raw = sigRef.current.getCanvas();
      const out = document.createElement("canvas");
      out.width = raw.width; out.height = raw.height;
      const ctx = out.getContext("2d");
      ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, out.width, out.height); ctx.drawImage(raw, 0, 0);
      const sigImage = out.toDataURL("image/jpeg", 0.7);
      if (!sigImage || sigImage.length < 1000) { setError("Umukono ntabwo wabashije gufatwa."); setSigning(false); return; }
      await axiosInstance.put(`/raporo/umudugudu-sign/${report.id}`, { signature_image: sigImage, signer_name: signerName });
      onSigned({ ...report, umudugudu_signature: sigImage, umudugudu_signed_at: new Date().toISOString(), umudugudu_signed_by: signerName });
      onClose();
    } catch (err) {
      const s = err?.response?.status;
      setError(s === 403 ? "Ntiwemerewe gusinya iyi raporo." : s === 404 ? "Raporo ntiboneka." : err?.response?.data?.error || "Gusinya byanze.");
    } finally { setSigning(false); }
  };

  return (
    <div onClick={onClose} style={{ position:"fixed", inset:0, zIndex:2000, background:"rgba(15,23,42,0.75)", backdropFilter:"blur(8px)", display:"flex", alignItems:"center", justifyContent:"center", padding:"1rem" }}>
      <motion.div initial={{ scale:0.9, opacity:0 }} animate={{ scale:1, opacity:1 }} exit={{ scale:0.9, opacity:0 }}
        onClick={e => e.stopPropagation()}
        style={{ background:"#fff", borderRadius:20, padding:"1.75rem", width:"100%", maxWidth:480, boxShadow:"0 25px 60px rgba(0,0,0,0.25)" }}>
        <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:"1.25rem" }}>
          <div style={{ width:44, height:44, borderRadius:14, background:"linear-gradient(135deg,#0f766e,#0d9488)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"1.2rem" }}>✍️</div>
          <div>
            <h5 style={{ fontWeight:800, margin:0, fontSize:"1rem", color:"#1e293b" }}>Shyiraho Umukono w'Umudugudu</h5>
            <p style={{ margin:0, fontSize:"0.78rem", color:"#64748b" }}>{report.title}</p>
          </div>
          <button onClick={onClose} style={{ marginLeft:"auto", background:"none", border:"none", fontSize:"1.3rem", cursor:"pointer", color:"#94a3b8" }}>✕</button>
        </div>
        <div style={{ fontSize:"0.72rem", fontWeight:700, color:"#0f766e", textTransform:"uppercase", letterSpacing:"0.5px", marginBottom:8 }}>Shyiraho Umukono Wawe</div>
        <div style={{ border:"2px dashed #0f766e", borderRadius:12, background:"#f0fdf4", overflow:"hidden", position:"relative" }}>
          <SignatureCanvas ref={sigRef} penColor="#0f766e"
            canvasProps={{ width:440, height:150, style:{ width:"100%", height:150, cursor:"crosshair", touchAction:"none" } }} />
          <div style={{ position:"absolute", top:"50%", left:"50%", transform:"translate(-50%,-50%)", fontSize:"0.75rem", color:"#94a3b8", pointerEvents:"none", userSelect:"none" }}>Shyiraho umukono hano ✍️</div>
        </div>
        {error && <div style={{ background:"#fee2e2", border:"1px solid #fecaca", borderRadius:10, padding:"0.65rem 1rem", marginTop:"0.75rem", fontSize:"0.85rem", color:"#dc2626", fontWeight:600 }}>⚠️ {error}</div>}
        <div style={{ display:"flex", gap:8, marginTop:"1rem" }}>
          <button onClick={() => sigRef.current?.clear()} style={{ flex:1, padding:"0.65rem", border:"2px solid #e2e8f0", borderRadius:10, background:"#f8fafc", color:"#475569", fontWeight:700, cursor:"pointer", fontSize:"0.85rem" }}>🗑 Siba</button>
          <button onClick={onClose} style={{ flex:1, padding:"0.65rem", border:"2px solid #e2e8f0", borderRadius:10, background:"#fff", color:"#64748b", fontWeight:700, cursor:"pointer", fontSize:"0.85rem" }}>Reka</button>
          <button onClick={handleSign} disabled={signing}
            style={{ flex:2, padding:"0.65rem", border:"none", borderRadius:10, background: signing ? "#94a3b8" : "linear-gradient(135deg,#0f766e,#0d9488)", color:"#fff", fontWeight:800, cursor: signing ? "not-allowed" : "pointer", fontSize:"0.85rem" }}>
            {signing ? "⏳ Gusinya..." : "✅ Sinyisha"}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

/* ── Main Component ── */
const RaporoZoherejwe = () => {
  const [reports, setReports]   = useState([]);
  const [loading, setLoading]   = useState(false);
  const [search, setSearch]     = useState("");
  const [selected, setSelected] = useState(null);
  const [signing, setSigning]   = useState(null);
  const [error, setError]       = useState(null);
  const [forwarding, setForwarding] = useState(null); // id being forwarded
  const [deleting, setDeleting]     = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [toast, setToast]       = useState(null);

  // Isibo navigation — click an Isibo to see only its reports.
  const [isibos, setIsibos] = useState([]);
  const [isiboFilter, setIsiboFilter] = useState(null); // null = all, "mine" = own reports

  useEffect(() => {
    axiosInstance.get("/raporo/isibo-list")
      .then((res) => setIsibos(Array.isArray(res.data) ? res.data : []))
      .catch(() => setIsibos([]));
  }, []);

  const notify = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchReports = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await axiosInstance.get("/raporo/isibo-reports", {
        params: isiboFilter && isiboFilter !== "mine" ? { isibo_id: isiboFilter } : {},
      });
      const rows = Array.isArray(res.data) ? res.data : [];
      setReports(isiboFilter === "mine" ? rows.filter(r => !!r.is_own) : rows);
    } catch {
      setError("Ntibyashobotse kubona raporo. Gerageza nanone.");
    } finally { setLoading(false); }
  }, [isiboFilter]);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  const handleSigned = (updated) => {
    setReports(prev => prev.map(r => r.id === updated.id ? updated : r));
    setSigning(null);
  };

  const handleDelete = (r) => setConfirmDelete(r);

  const execDelete = async () => {
    const r = confirmDelete;
    setConfirmDelete(null);
    setDeleting(r.id);
    try {
      await axiosInstance.delete(`/raporo/${r.id}/mudugudu-delete`);
      setReports(prev => prev.filter(x => x.id !== r.id));
      if (selected?.id === r.id) setSelected(null);
      notify("🗑️ Raporo yasibwe neza!");
    } catch (err) {
      notify(err?.response?.data?.error || "Gusiba byanze. Gerageza nanone.", "error");
    } finally { setDeleting(null); }
  };

  const handleForward = async (r) => {
    if (forwarding) return;
    setForwarding(r.id);
    try {
      await axiosInstance.post(`/raporo/${r.id}/forward-to-akagari`);
      setReports(prev => prev.map(x => x.id === r.id ? { ...x, forwarded_to_akagari: 1, forwarded_at: new Date().toISOString(), status: "Itegereje Akagari" } : x));
      notify("✅ Raporo yoherejwe kuri Akagari neza!");
      if (selected?.id === r.id) setSelected(s => ({ ...s, forwarded_to_akagari: 1 }));
    } catch (err) {
      notify(err?.response?.data?.error || "Kohereza byanze. Gerageza nanone.", "error");
    } finally { setForwarding(null); }
  };

  const filtered = reports.filter(r => {
    const q = search.toLowerCase();
    return (r.title || "").toLowerCase().includes(q) || (r.isibo_name || "").toLowerCase().includes(q) || (r.description || "").toLowerCase().includes(q);
  });

  return (
    <div className="space-y-4 rz-page">
      <style>{`
        /* The global .card is a dark glass style; this page is light, so use white cards */
        .rz-page .card { background: #ffffff; border: 1px solid #e2e8f0; border-radius: 16px;
          backdrop-filter: none; -webkit-backdrop-filter: none; box-shadow: 0 2px 10px rgba(15,23,42,0.05); }
        .rz-pill { white-space: nowrap; display: inline-flex; align-items: center; gap: 4px; line-height: 1.5; }
        .rz-chips { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; padding: 0 0.25rem; }
        .rz-chips > button { white-space: nowrap; flex-shrink: 0; }
        @media (max-width: 575px) {
          /* filter chips: one swipeable row instead of a tall stack */
          .rz-chips { flex-wrap: nowrap; overflow-x: auto; scrollbar-width: none; padding-bottom: 4px;
            margin: 0 -0.25rem; -webkit-overflow-scrolling: touch; }
          .rz-chips::-webkit-scrollbar { display: none; }
          .rz-page .rz-card-body { padding: 0.9rem 1rem !important; }
          .rz-page .rz-empty { padding: 2.5rem 1.25rem !important; }
          /* buttons get their own full-width row below the details */
          .rz-actions { width: 100%; padding-top: 10px; border-top: 1px solid #eef2f7; }
          .rz-actions > * { flex: 1 1 auto; justify-content: center; }
          .rz-modal-body { padding: 1.25rem !important; }
          .rz-modal-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
      {/* Toast */}
      {toast && (
        <div style={{ position:"fixed", top:20, right:20, zIndex:9999, padding:"12px 20px", borderRadius:12, fontWeight:600, fontSize:14,
          background: toast.type === "success" ? "#ecfdf5" : "#fef2f2",
          color: toast.type === "success" ? "#065f46" : "#991b1b",
          border: `1px solid ${toast.type === "success" ? "#a7f3d0" : "#fecaca"}`,
          boxShadow:"0 4px 20px rgba(0,0,0,0.1)" }}>
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div className="page-header">
        <h2 className="page-title flex items-center gap-2">
          <i className="bi bi-file-earmark-arrow-down-fill" style={{ color:"#1d4ed8" }}></i>
          Raporo Zoherejwe
        </h2>
        <p className="page-subtitle">Raporo wanditse n'izo Isibo zohereje kuri Umudugudu</p>
      </div>

      {/* Navigation — all reports, the leader's own, or one Isibo's */}
      <div className="rz-chips">
        <button onClick={() => setIsiboFilter(null)}
          style={{ padding:"0.4rem 0.9rem", borderRadius:20, fontSize:"0.8rem", fontWeight:700, cursor:"pointer",
            border: `1px solid ${!isiboFilter ? "#1d4ed8" : "#e2e8f0"}`,
            background: !isiboFilter ? "#1d4ed8" : "#f8fafc",
            color: !isiboFilter ? "#fff" : "#475569" }}>
          Zose
        </button>
        <button onClick={() => setIsiboFilter("mine")}
          style={{ padding:"0.4rem 0.9rem", borderRadius:20, fontSize:"0.8rem", fontWeight:700, cursor:"pointer",
            border: `1px solid ${isiboFilter === "mine" ? "#1d4ed8" : "#e2e8f0"}`,
            background: isiboFilter === "mine" ? "#1d4ed8" : "#f8fafc",
            color: isiboFilter === "mine" ? "#fff" : "#475569" }}>
          Izo Nanditse
        </button>
        {isibos.map((s) => (
          <button key={s.isibo_id} onClick={() => setIsiboFilter(s.isibo_id)}
            style={{ padding:"0.4rem 0.9rem", borderRadius:20, fontSize:"0.8rem", fontWeight:700, cursor:"pointer",
              border: `1px solid ${isiboFilter === s.isibo_id ? "#1d4ed8" : "#e2e8f0"}`,
              background: isiboFilter === s.isibo_id ? "#1d4ed8" : "#f8fafc",
              color: isiboFilter === s.isibo_id ? "#fff" : "#475569" }}>
            {s.isibo_name}
          </button>
        ))}
      </div>

      {/* Toolbar */}
      <div className="card">
        <div className="card-body" style={{ padding:"0.875rem 1.25rem" }}>
          <div style={{ display:"flex", gap:10, flexWrap:"wrap", alignItems:"center" }}>
            <div style={{ display:"flex", alignItems:"center", gap:8, flex:"1 1 220px", background:"#f8fafc", border:"1px solid #e2e8f0", borderRadius:10, padding:"0 12px", minWidth:0 }}>
              <i className="bi bi-search" style={{ color:"#94a3b8", fontSize:"0.9rem" }}></i>
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Shakisha raporo..."
                style={{ border:"none", background:"transparent", outline:"none", padding:"0.6rem 0.25rem", fontSize:"0.88rem", color:"#1e293b", width:"100%" }} />
              {search && <button onClick={() => setSearch("")} style={{ background:"none", border:"none", cursor:"pointer", color:"#94a3b8", padding:0 }}><i className="bi bi-x-circle-fill"></i></button>}
            </div>
            <span style={{ padding:"0.45rem 0.9rem", background:"#dbeafe", color:"#1d4ed8", borderRadius:20, fontSize:"0.8rem", fontWeight:700, whiteSpace:"nowrap" }}>
              {filtered.length} / {reports.length} raporo
            </span>
            <button onClick={fetchReports} disabled={loading}
              style={{ display:"flex", alignItems:"center", gap:6, padding:"0.55rem 1rem", background:"#f8fafc", border:"1px solid #e2e8f0", borderRadius:10, cursor:"pointer", fontSize:"0.85rem", color:"#475569", fontWeight:600 }}>
              <i className="bi bi-arrow-clockwise"></i> Vugurura
            </button>
          </div>
        </div>
      </div>

      {loading && <div style={{ textAlign:"center", padding:"4rem 0" }}><div className="spinner-border text-primary" style={{ width:"3rem", height:"3rem" }} /><p style={{ marginTop:"1rem", color:"#64748b" }}>Raporo zirimo gutegurwa...</p></div>}

      {!loading && error && (
        <div className="card"><div className="card-body" style={{ textAlign:"center", padding:"3rem" }}>
          <i className="bi bi-exclamation-triangle-fill" style={{ fontSize:"2.5rem", color:"#dc2626" }}></i>
          <p style={{ marginTop:"1rem", color:"#dc2626", fontWeight:600 }}>{error}</p>
          <button onClick={fetchReports} style={{ marginTop:"1rem", padding:"0.5rem 1.25rem", background:"#1d4ed8", color:"#fff", border:"none", borderRadius:8, cursor:"pointer", fontWeight:600 }}>
            <i className="bi bi-arrow-clockwise me-1"></i> Gerageza nanone
          </button>
        </div></div>
      )}

      {!loading && !error && filtered.length === 0 && (
        <div className="card"><div className="card-body rz-empty" style={{ textAlign:"center", padding:"5rem 2rem" }}>
          <div style={{ width:90, height:90, borderRadius:"50%", background:"#dbeafe", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 1.5rem" }}>
            <i className="bi bi-file-earmark-x" style={{ fontSize:"2.5rem", color:"#1d4ed8" }}></i>
          </div>
          <h5 style={{ fontWeight:700, color:"#1e293b" }}>{search ? "Nta raporo ibonetse" : "Nta raporo yoherejwe"}</h5>
          <p style={{ color:"#64748b", maxWidth:360, margin:"0.5rem auto 0" }}>{search ? "Gerageza amagambo atandukanye." : isiboFilter === "mine" ? "Nta raporo uranditse." : "Nta raporo irandikwa cyangwa ngo yoherezwe kuri Umudugudu."}</p>
        </div></div>
      )}

      {!loading && !error && filtered.length > 0 && (
        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
          {filtered.map((r, i) => {
            const isForwarded = !!r.forwarded_to_akagari;
            const isFwding = forwarding === r.id;

            return (
              <motion.div key={r.id || i}
                initial={{ opacity:0, y:10 }} animate={{ opacity:1, y:0 }} transition={{ delay: i * 0.04 }}
                className="card" style={{ cursor:"pointer", transition:"box-shadow 0.2s" }}
                onClick={() => setSelected(r)}
                onMouseEnter={e => e.currentTarget.style.boxShadow = "0 6px 20px rgba(29,78,216,0.12)"}
                onMouseLeave={e => e.currentTarget.style.boxShadow = ""}>
                <div className="card-body rz-card-body" style={{ padding:"1rem 1.25rem" }}>
                  <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:12, flexWrap:"wrap" }}>
                    {/* basis 260px: on phones the buttons wrap below instead of squeezing this */}
                    <div style={{ flex:"1 1 260px", minWidth:0 }}>
                      {/* Title + status */}
                      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6, flexWrap:"wrap" }}>
                        <div style={{ width:34, height:34, borderRadius:10, background:"#dbeafe", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                          <i className="bi bi-file-earmark-text-fill" style={{ color:"#1d4ed8", fontSize:"0.95rem" }}></i>
                        </div>
                        <h6 style={{ fontWeight:700, margin:0, color:"#1e293b", fontSize:"0.95rem", flex:"1 1 160px", minWidth:0, overflowWrap:"anywhere" }}>{r.title || "Raporo"}</h6>
                        <span className="rz-pill" style={{ fontSize:"0.72rem", fontWeight:700, padding:"3px 10px", borderRadius:20,
                          background: r.status === "Yemejwe Burundu" ? "#dcfce7" : r.status === "Itegereje Akagari" ? "#dbeafe" : "#fef3c7",
                          color: r.status === "Yemejwe Burundu" ? "#15803d" : r.status === "Itegereje Akagari" ? "#1d4ed8" : "#b45309",
                          border: `1px solid ${r.status === "Yemejwe Burundu" ? "#bbf7d0" : r.status === "Itegereje Akagari" ? "#bfdbfe" : "#fde68a"}` }}>
                          {r.status || "Yoherejwe"}
                        </span>
                        {/* Forward status badge */}
                        {isForwarded
                          ? <span className="rz-pill" style={{ fontSize:"0.72rem", fontWeight:700, padding:"3px 10px", borderRadius:20, background:"#dcfce7", color:"#15803d", border:"1px solid #bbf7d0" }}>🟢 Yoherejwe Akagari</span>
                          : <span className="rz-pill" style={{ fontSize:"0.72rem", fontWeight:700, padding:"3px 10px", borderRadius:20, background:"#f1f5f9", color:"#64748b", border:"1px solid #e2e8f0" }}>🔴 Ntiyoherejwe</span>
                        }
                      </div>
                      {/* Sig badges */}
                      <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:6 }}>
                        {!r.is_own && <SigBadge signed={!!r.isibo_signature} label="Isibo Yasinyye" />}
                        <SigBadge signed={!!r.umudugudu_signature} label="Umudugudu Yasinyye" />
                      </div>
                      {/* Meta */}
                      <div style={{ display:"flex", gap:14, flexWrap:"wrap" }}>
                        <span style={{ fontSize:"0.8rem", color:"#64748b", display:"flex", alignItems:"center", gap:4 }}>
                          <i className="bi bi-person-fill" style={{ color:"#1d4ed8" }}></i>{r.isibo_name || "—"}
                        </span>
                        <span style={{ fontSize:"0.8rem", color:"#64748b", display:"flex", alignItems:"center", gap:4 }}>
                          <i className="bi bi-calendar-fill" style={{ color:"#b45309" }}></i>{fmtDate(r.date)}
                        </span>
                        {isForwarded && r.forwarded_at && (
                          <span style={{ fontSize:"0.8rem", color:"#15803d", display:"flex", alignItems:"center", gap:4 }}>
                            <i className="bi bi-send-fill"></i>Yoherejwe: {fmtDate(r.forwarded_at)}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Action buttons */}
                    <div className="rz-actions" style={{ display:"flex", gap:6, flexShrink:0, flexWrap:"wrap" }} onClick={e => e.stopPropagation()}>
                      {/* View */}
                      <button onClick={() => setSelected(r)}
                        style={{ display:"flex", alignItems:"center", gap:5, padding:"0.5rem 0.875rem", background:"linear-gradient(135deg,#1d4ed8,#0369a1)", border:"none", borderRadius:8, color:"#fff", fontWeight:600, fontSize:"0.8rem", cursor:"pointer" }}>
                        <i className="bi bi-eye-fill"></i> Soma
                      </button>
                      {/* Sign */}
                      {!r.umudugudu_signature && (
                        <button onClick={() => setSigning(r)}
                          style={{ display:"flex", alignItems:"center", gap:5, padding:"0.5rem 0.875rem", background:"linear-gradient(135deg,#0f766e,#0d9488)", border:"none", borderRadius:8, color:"#fff", fontWeight:700, fontSize:"0.8rem", cursor:"pointer" }}>
                          ✏️ Sinyisha
                        </button>
                      )}
                      {/* Delete (only when forwarded) */}
                      {isForwarded && (
                        <button onClick={() => handleDelete(r)} disabled={deleting === r.id}
                          style={{ display:"flex", alignItems:"center", gap:5, padding:"0.5rem 0.875rem",
                            background: deleting === r.id ? "#94a3b8" : "linear-gradient(135deg,#dc2626,#b91c1c)",
                            border:"none", borderRadius:8, color:"#fff", fontWeight:700, fontSize:"0.8rem",
                            cursor: deleting === r.id ? "not-allowed" : "pointer" }}>
                          {deleting === r.id ? <>⏳ Gusiba...</> : <>🗑️ Siba</>}
                        </button>
                      )}
                      {/* Forward to Akagari */}
                      {!isForwarded ? (
                        <button
                          onClick={() => handleForward(r)}
                          disabled={isFwding}
                          style={{ display:"flex", alignItems:"center", gap:5, padding:"0.5rem 0.875rem",
                            background: isFwding ? "#94a3b8" : "linear-gradient(135deg,#7c3aed,#6d28d9)",
                            border:"none", borderRadius:8, color:"#fff", fontWeight:700, fontSize:"0.8rem",
                            cursor: isFwding ? "not-allowed" : "pointer", boxShadow:"0 3px 10px rgba(124,58,237,0.3)" }}>
                          {isFwding
                            ? <><span className="spinner-border spinner-border-sm" style={{ width:"0.8rem", height:"0.8rem" }}></span> Kohereza...</>
                            : <><i className="bi bi-send-fill"></i> Ohereza Akagari</>}
                        </button>
                      ) : (
                        <span style={{ display:"flex", alignItems:"center", gap:5, padding:"0.5rem 0.875rem", background:"#dcfce7", border:"1px solid #bbf7d0", borderRadius:8, color:"#15803d", fontWeight:700, fontSize:"0.8rem" }}>
                          ✅ Yoherejwe
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Detail Modal */}
      <AnimatePresence>
        {selected && (
          <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
            onClick={() => setSelected(null)}
            style={{ position:"fixed", inset:0, zIndex:1500, background:"rgba(15,23,42,0.75)", backdropFilter:"blur(8px)", display:"flex", alignItems:"flex-start", justifyContent:"center", padding:"1rem", overflowY:"auto" }}>
            <motion.div initial={{ scale:0.95, y:20, opacity:0 }} animate={{ scale:1, y:0, opacity:1 }} exit={{ scale:0.95, opacity:0 }}
              onClick={e => e.stopPropagation()}
              style={{ width:"100%", maxWidth:680, background:"#fff", margin:"2rem auto", borderRadius:16, boxShadow:"0 8px 32px rgba(0,0,0,0.15)", overflow:"hidden" }}>
              <div style={{ display:"flex", gap:8, padding:"0.75rem 1rem", borderBottom:"1px solid #e2e8f0", background:"#f8fafc", justifyContent:"flex-end", flexWrap:"wrap" }}>
                {!selected.umudugudu_signature && (
                  <button onClick={() => { setSelected(null); setSigning(selected); }}
                    style={{ display:"flex", alignItems:"center", gap:6, padding:"0.5rem 1rem", background:"linear-gradient(135deg,#0f766e,#0d9488)", color:"#fff", border:"none", borderRadius:8, fontWeight:700, fontSize:"0.82rem", cursor:"pointer" }}>
                    ✏️ Sinyisha
                  </button>
                )}
                {!selected.forwarded_to_akagari && (
                  <button onClick={() => { handleForward(selected); setSelected(null); }}
                    disabled={forwarding === selected.id}
                    style={{ display:"flex", alignItems:"center", gap:6, padding:"0.5rem 1rem", background:"linear-gradient(135deg,#7c3aed,#6d28d9)", color:"#fff", border:"none", borderRadius:8, fontWeight:700, fontSize:"0.82rem", cursor:"pointer" }}>
                    <i className="bi bi-send-fill"></i> Ohereza Akagari
                  </button>
                )}
                {selected.forwarded_to_akagari && (
                  <>
                    <span style={{ display:"flex", alignItems:"center", gap:6, padding:"0.5rem 1rem", background:"#dcfce7", border:"1px solid #bbf7d0", borderRadius:8, color:"#15803d", fontWeight:700, fontSize:"0.82rem" }}>
                      ✅ Yoherejwe Akagari
                    </span>
                    <button onClick={() => { setSelected(null); handleDelete(selected); }} disabled={deleting === selected.id}
                      style={{ display:"flex", alignItems:"center", gap:6, padding:"0.5rem 1rem",
                        background: deleting === selected.id ? "#94a3b8" : "linear-gradient(135deg,#dc2626,#b91c1c)",
                        color:"#fff", border:"none", borderRadius:8, fontWeight:700, fontSize:"0.82rem", cursor:"pointer" }}>
                      🗑️ Siba
                    </button>
                  </>
                )}
                <button onClick={() => setSelected(null)} style={{ padding:"0.5rem 1rem", background:"#ef4444", color:"#fff", border:"none", borderRadius:8, fontWeight:600, fontSize:"0.82rem", cursor:"pointer" }}>✕ Funga</button>
              </div>
              <div className="rz-modal-body" style={{ padding:"1.75rem" }}>
                <h4 style={{ fontWeight:800, color:"#1e293b", marginBottom:"1rem" }}>{selected.title || "Raporo"}</h4>
                <div className="rz-modal-grid" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0.75rem", marginBottom:"1rem", padding:"1rem", background:"#f8fafc", borderRadius:10, border:"1px solid #e2e8f0" }}>
                  {[[selected.is_own ? "👤 Uwanditse" : "👤 Isibo", selected.isibo_name], ["📅 Itariki", fmtDate(selected.date)], ["📤 Uwo Igenewe", selected.recipient || "Umudugudu"], ["🏷 Statut", selected.status || "—"]].map(([l, v]) => (
                    <div key={l}><div style={{ fontSize:"0.68rem", fontWeight:700, color:"#64748b", textTransform:"uppercase", marginBottom:3 }}>{l}</div><div style={{ fontSize:"0.9rem", fontWeight:600, color:"#1e293b" }}>{v || "—"}</div></div>
                  ))}
                </div>
                <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:"1rem" }}>
                  {!selected.is_own && <SigBadge signed={!!selected.isibo_signature} label="Isibo Yasinyye" />}
                  <SigBadge signed={!!selected.umudugudu_signature} label="Umudugudu Yasinyye" />
                  {selected.forwarded_to_akagari
                    ? <span className="rz-pill" style={{ display:"inline-flex", alignItems:"center", gap:4, padding:"3px 10px", borderRadius:20, background:"#dcfce7", color:"#15803d", border:"1px solid #bbf7d0", fontSize:"0.72rem", fontWeight:700 }}>🟢 Yoherejwe Akagari</span>
                    : <span className="rz-pill" style={{ display:"inline-flex", alignItems:"center", gap:4, padding:"3px 10px", borderRadius:20, background:"#f1f5f9", color:"#64748b", border:"1px solid #e2e8f0", fontSize:"0.72rem", fontWeight:700 }}>🔴 Ntiyoherejwe Akagari</span>
                  }
                </div>
                <div style={{ lineHeight:1.9, fontSize:"0.95rem", color:"#334155", whiteSpace:"pre-wrap", wordWrap:"break-word" }}>
                  {selected.description || "Nta bisobanuro."}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Confirm Delete Modal */}
      <AnimatePresence>
        {confirmDelete && (
          <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
            style={{ position:"fixed", inset:0, zIndex:3000, background:"rgba(15,23,42,0.75)", backdropFilter:"blur(8px)", display:"flex", alignItems:"center", justifyContent:"center", padding:"1rem" }}>
            <motion.div initial={{ scale:0.9, opacity:0 }} animate={{ scale:1, opacity:1 }} exit={{ scale:0.9, opacity:0 }}
              style={{ background:"#fff", borderRadius:20, padding:"2rem", width:"100%", maxWidth:420, boxShadow:"0 25px 60px rgba(0,0,0,0.25)", textAlign:"center" }}>
              <div style={{ width:60, height:60, borderRadius:"50%", background:"#fee2e2", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 1.25rem", fontSize:"1.75rem" }}>🗑️</div>
              <h5 style={{ fontWeight:800, color:"#1e293b", marginBottom:"0.5rem" }}>Gusiba Raporo</h5>
              <p style={{ color:"#64748b", fontSize:"0.9rem", marginBottom:"0.25rem" }}>Ushaka gusiba raporo:</p>
              <p style={{ fontWeight:700, color:"#dc2626", fontSize:"0.95rem", marginBottom:"1.5rem", padding:"0.5rem 1rem", background:"#fee2e2", borderRadius:8 }}>« {confirmDelete.title} »</p>
              <p style={{ color:"#94a3b8", fontSize:"0.8rem", marginBottom:"1.5rem" }}>Ibi ntibishobora gusubizwaho.</p>
              <div style={{ display:"flex", gap:10 }}>
                <button onClick={() => setConfirmDelete(null)}
                  style={{ flex:1, padding:"0.7rem", border:"2px solid #e2e8f0", borderRadius:10, background:"#f8fafc", color:"#475569", fontWeight:700, cursor:"pointer", fontSize:"0.88rem" }}>Reka</button>
                <button onClick={execDelete}
                  style={{ flex:1, padding:"0.7rem", border:"none", borderRadius:10, background:"linear-gradient(135deg,#dc2626,#b91c1c)", color:"#fff", fontWeight:800, cursor:"pointer", fontSize:"0.88rem" }}>🗑️ Yego, Siba</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Sign Modal */}
      <AnimatePresence>
        {signing && <SignModal report={signing} onClose={() => setSigning(null)} onSigned={handleSigned} />}
      </AnimatePresence>
    </div>
  );
};

export default RaporoZoherejwe;
